<template>
  <v-dialog v-model="dialog" persistent scrollable max-width="680px">
    <v-card class="profile-dialog-card entity-form-dialog">
      <div class="profile-dialog-header entity-form-dialog__header">
        <div class="entity-form-dialog__title">
          <v-avatar size="44" color="green lighten-5">
            <v-icon color="primary">mdi-hand-heart-outline</v-icon>
          </v-avatar>
          <div>
            <span>Donors</span>
            <h2>New donor</h2>
            <p>Add contact details and mailing information.</p>
          </div>
        </div>
        <v-btn icon aria-label="Close new donor dialog" @click.stop="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-divider />

      <v-card-text class="profile-dialog-body entity-form-dialog__body">
        <section class="entity-form-dialog__section">
          <div class="entity-form-dialog__section-heading">
            <span><v-icon size="19" color="primary">mdi-account-outline</v-icon></span>
            <div>
              <h3>Donor information</h3>
              <p>Primary contact details for this donor.</p>
            </div>
          </div>

          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="donor.name"
                outlined
                dense
                autofocus
                :error-messages="nameErrors"
                :hide-details="nameErrors.length === 0"
                label="Full name"
                @change="donor.name = properCase(donor.name)"
                @focus="$event.target.select()"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="donor.phoneNumber"
                v-mask="'(###) ###-####'"
                outlined
                dense
                hide-details
                label="Phone number"
                @focus="$event.target.select()"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="donor.email"
                outlined
                dense
                hide-details
                label="Email address"
              />
            </v-col>
          </v-row>
        </section>

        <section class="entity-form-dialog__section">
          <div class="entity-form-dialog__section-heading">
            <span><v-icon size="19" color="primary">mdi-map-marker-outline</v-icon></span>
            <div>
              <h3>Address and notes</h3>
              <p>Optional mailing address and internal notes.</p>
            </div>
          </div>

          <v-row>
            <v-col cols="12">
              <v-text-field v-model="donor.address" outlined dense hide-details label="Street address" />
            </v-col>
            <v-col cols="12" sm="5">
              <v-text-field
                v-model="donor.city"
                outlined
                dense
                hide-details
                label="City"
                @change="donor.city = properCase(donor.city)"
              />
            </v-col>
            <v-col cols="6" sm="3">
              <v-text-field
                v-model="donor.state"
                v-mask="'AA'"
                outlined
                dense
                hide-details
                label="State"
              />
            </v-col>
            <v-col cols="6" sm="4">
              <v-text-field
                v-model="donor.zip"
                v-mask="'#####'"
                outlined
                dense
                hide-details
                label="ZIP code"
              />
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="donor.note"
                outlined
                dense
                hide-details
                auto-grow
                rows="2"
                :label="$t('labels.note')"
              />
            </v-col>
          </v-row>
        </section>
      </v-card-text>

      <v-divider />

      <v-card-actions class="profile-dialog-actions entity-form-dialog__actions">
        <div class="entity-form-dialog__status">
          <span>Active</span>
          <toggle-button
            v-model="donor.isActive"
            :width="60"
            :labels="{ checked: 'Show', unchecked: 'Hide' }"
            :value="donor.isActive"
            :sync="true"
          />
        </div>
        <v-spacer />
        <v-btn text @click="close()">Cancel</v-btn>
        <v-btn color="primary" depressed :loading="loading" @click="submit()">
          <v-icon left small>mdi-account-plus-outline</v-icon>
          Create donor
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import donorService from "@/modules/Donors/service.js";
import GlobalFunction from "@/mixins/GlobalFunction";

const { validationMixin } = require("vuelidate");
const { required } = require("vuelidate/lib/validators");

export default {
  props: {
    createDialog: {
      default: false,
    },
    createFromStore: {
      type: Boolean,
    },
  },
  name: "create-donor",
  data() {
    return {
      foundError: false,
      loading: false,
      dialog: false,
      donor: {
        isActive: true,
        name: null,
        phoneNumber: null,
        emailAddress: null,
        address: null,
        city: null,
        state: null,
        zip: null,
        note: null,
      },
    };
  },
  mixins: [validationMixin, GlobalFunction],
  validations: {
    donor: {
      name: {
        required,
      },
    },
  },
  computed: {
    ...mapGetters("global", ["currentUser"]),
    nameErrors() {
      const errors = [];
      if (!this.$v.donor.name.$dirty) return errors;
      if (!this.$v.donor.name.required)
        errors.push(this.$t("validations.fieldIsRequired"));

      return errors;
    },
  },
  watch: {
    createDialog(val) {
      this.dialog = val;
    },
  },
  methods: {
    // properCase(string) {
    //   var sentence = string.toLowerCase().split(" ");
    //   for (var i = 0; i < sentence.length; i++) {
    //     sentence[i] = sentence[i][0].toUpperCase() + sentence[i].slice(1);
    //   }
    //   return sentence.join(" ");
    // },
    close() {
      this.empty();
      this.$emit("close");
    },
    empty() {
      this.donor = {
        isActive: true,
        name: null,
        phoneNumber: null,
        emailAddress: null,
        address: null,
        city: null,
        state: null,
        zip: null,
        note: null,
      };
      this.$v.$reset();
    },
    submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });
      } else {
        this.loading = true;
        if (this.donor.phoneNumber) {
          this.donor.phoneNumber = this.donor.phoneNumber.replace(/\D/g, "");
        }
        return donorService
          .create(this.donor)
          .then((result) => {
            if (result.status == 409) {
              this.$swal
                .mixin({
                  toast: true,
                  position: "top-end",
                  showConfirmButton: false,
                  timer: 3000,
                })
                .fire({
                  icon: "error",
                  title: result.data.message,
                });
                this.loading = false;
                return false
            }
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "success",
                title: "Donor is created",
              });
            this.loading = false;
            this.$emit("submit");
            // this.$events.emit("customerListEvent");
            this.close();
          })
          .catch((err) => {
            console.log("err", err);
            this.loading = false;
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "error",
                title: err.data.message,
              });
            this.close();
          });
      }
    },
  },
};
</script>

<style scoped></style>
